<script setup>
// FinishSetup.vue
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import EmptyState from '../../../../components/widgets/EmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

// Store, rota e i18n
const store = useStore();
const route = useRoute();
const { t } = useI18n();

// Estado para QR Code
const qrcodeData = ref(null);
const qrcodeError = ref(null);
const isLoadingQR = ref(false);

// Inbox atual
const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](route.params.inbox_id)
);

// Tipos de canais
const isATwilioInbox = computed(
  () => currentInbox.value?.channel_type === 'Channel::TwilioSms'
);

const isApiInbox = computed(
  () => currentInbox.value?.channel_type === 'Channel::Api'
);

const isAEmailInbox = computed(
  () => currentInbox.value?.channel_type === 'Channel::Email'
);

const isALineInbox = computed(
  () => currentInbox.value?.channel_type === 'Channel::Line'
);

const isASmsInbox = computed(
  () => currentInbox.value?.channel_type === 'Channel::Sms'
);

const isWhatsAppCloudInbox = computed(
  () =>
    currentInbox.value?.channel_type === 'Channel::Whatsapp' &&
    ['whatsapp_cloud', 'whatsapp_coexistence'].includes(
      currentInbox.value?.provider
    )
);

const isInstagramInbox = computed(
  () => currentInbox.value?.channel_type === 'Channel::Instagram'
);

const isFacebookInbox = computed(
  () => currentInbox.value?.channel_type === 'Channel::FacebookPage'
);

// Computed para verificar se está conectado
const isConnected = computed(
  () =>
    qrcodeData.value?.data?.connected &&
    qrcodeData.value?.data?.status === 'open'
);

// Verificação de duplicata Instagram/Facebook
const hasDuplicateInstagramInbox = computed(() => {
  // Só verifica duplicata se for Instagram
  if (currentInbox.value?.channel_type !== INBOX_TYPES.INSTAGRAM) {
    return false;
  }

  const instagramId = currentInbox.value?.instagram_id;
  const facebookInbox =
    store.getters['inboxes/getFacebookInboxByInstagramId'](instagramId);

  return !!facebookInbox;
});

// Mensagem dinâmica usando t()
const message = computed(() => {
  if (isApiInbox.value) {
    if (isConnected.value) {
      return `${t('INBOX_MGMT.FINISH.MESSAGE')}. Sua instância WhatsApp está conectada e pronta para uso.`;
    }
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. Escaneie o QR Code abaixo com seu WhatsApp para conectar.`;
  }

  if (isATwilioInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isASmsInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.SMS.BANDWIDTH.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isALineInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isWhatsAppCloudInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAEmailInbox.value && !currentInbox.value?.provider) {
    return t('INBOX_MGMT.ADD.EMAIL_CHANNEL.FINISH_MESSAGE');
  }

  if (currentInbox.value?.web_widget_script) {
    return t('INBOX_MGMT.FINISH.WEBSITE_SUCCESS');
  }

  if (isInstagramInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}`;
  }

  if (isFacebookInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}`;
  }

  return t('INBOX_MGMT.FINISH.MESSAGE');
});

// Funções para QR Code
const loadQRCode = async () => {
  if (!isApiInbox.value) return;

  isLoadingQR.value = true;
  qrcodeError.value = null;

  try {
    const response = await store.dispatch(
      'inboxes/getQRCode',
      route.params.inbox_id
    );
    qrcodeData.value = response;
  } catch (error) {
    qrcodeError.value = error.message || 'Erro ao carregar QR Code';
  } finally {
    isLoadingQR.value = false;
  }
};

// Função para inicializar QR Code (verifica status primeiro)
const initializeQRCode = async () => {
  if (!isApiInbox.value) return;

  isLoadingQR.value = true;
  qrcodeError.value = null;

  try {
    const statusResponse = await store.dispatch(
      'inboxes/checkWhatsappStatus',
      route.params.inbox_id
    );

    if (
      statusResponse.data.connected &&
      statusResponse.data.status === 'open'
    ) {
      qrcodeData.value = {
        data: {
          status: statusResponse.data.status,
          connected: statusResponse.data.connected,
          instance_name: statusResponse.data.instance_name,
          qrcode_base64: null,
          pairing_code: null,
        },
      };
    } else {
      await loadQRCode();
    }
  } catch {
    await loadQRCode();
  } finally {
    isLoadingQR.value = false;
  }
};

const checkStatus = async () => {
  if (!isApiInbox.value) return;

  try {
    const response = await store.dispatch(
      'inboxes/checkWhatsappStatus',
      route.params.inbox_id
    );
    if (qrcodeData.value) {
      qrcodeData.value.data.status = response.data.status;
      qrcodeData.value.data.connected = response.data.connected;
    }
  } catch {
    // silencioso — botão fica disponível para retry manual
  }
};

const sleep = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const disconnectInstance = async () => {
  if (!isApiInbox.value) return;

  isLoadingQR.value = true;
  qrcodeError.value = null;

  try {
    const response = await store.dispatch(
      'inboxes/disconnectWhatsappInstance',
      route.params.inbox_id
    );

    if (response.success) {
      qrcodeData.value = null;
      await sleep(1500);
      await loadQRCode();
    } else {
      qrcodeError.value = response.error || 'Erro ao desconectar instância';
    }
  } catch (error) {
    qrcodeError.value = error.message || 'Erro ao desconectar instância';
  } finally {
    isLoadingQR.value = false;
  }
};

// Montar componente
onMounted(() => {
  if (isApiInbox.value) {
    initializeQRCode();
  }
});
</script>

<template>
  <div
    class="w-full h-full col-span-6 p-6 overflow-auto border border-b-0 rounded-t-lg border-n-weak bg-n-solid-1"
  >
    <DuplicateInboxBanner
      v-if="hasDuplicateInstagramInbox"
      :content="$t('INBOX_MGMT.ADD.INSTAGRAM.NEW_INBOX_SUGGESTION')"
    />
    <EmptyState
      :title="$t('INBOX_MGMT.FINISH.TITLE')"
      :message="message"
      :button-text="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
    >
      <div class="w-full text-center">
        <!-- QR Code para Channel::Api (WhatsApp) -->
        <div v-if="isApiInbox" class="mb-8 max-w-md mx-auto">
          <div
            v-if="isLoadingQR"
            class="flex flex-col justify-center items-center p-8"
          >
            <Spinner :size="32" class="text-woot-600 mb-4" />
            <div class="text-n-slate-11">
              {{ $t('INBOX_MGMT.FINISH.QR.LOADING') }}
            </div>
          </div>

          <div
            v-else-if="qrcodeError"
            class="p-4 bg-n-ruby-2 border border-n-ruby-6 rounded-lg text-center mb-6"
          >
            <div class="text-n-ruby-11 mb-4">{{ qrcodeError }}</div>
            <NextButton outline slate @click="loadQRCode">
              {{ $t('INBOX_MGMT.FINISH.QR.RETRY') }}
            </NextButton>
          </div>

          <div v-else-if="qrcodeData" class="text-center">
            <div v-if="isConnected" class="mb-6">
              <div
                class="w-16 h-16 mx-auto bg-n-teal-2 border border-n-teal-6 rounded-full flex items-center justify-center mb-4"
              >
                <svg
                  class="w-8 h-8 text-n-teal-11"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-n-slate-12 mb-4">
                {{ $t('INBOX_MGMT.FINISH.QR.CONNECTED') }}
              </h4>
              <div class="flex justify-center gap-3 mb-6">
                <NextButton outline slate @click="checkStatus">
                  {{ $t('INBOX_MGMT.FINISH.QR.CHECK_STATUS') }}
                </NextButton>
                <NextButton outline ruby @click="disconnectInstance">
                  {{ $t('INBOX_MGMT.FINISH.QR.DISCONNECT') }}
                </NextButton>
              </div>
            </div>

            <div v-else class="mb-6">
              <div class="qr-wrapper mb-6">
                <img
                  v-if="qrcodeData.data.qrcode_base64"
                  :src="qrcodeData.data.qrcode_base64"
                  alt="QR Code WhatsApp"
                  class="qr-image"
                />
                <div v-else class="qr-placeholder">
                  <div class="text-n-slate-9 mb-2">
                    {{ $t('INBOX_MGMT.FINISH.QR.UNAVAILABLE') }}
                  </div>
                  <div class="text-xs text-n-slate-11">
                    {{ $t('INBOX_MGMT.FINISH.QR.CHECK_INSTANCE') }}
                  </div>
                </div>
              </div>
              <div class="flex justify-center gap-3 mb-6">
                <NextButton outline slate @click="loadQRCode">
                  {{ $t('INBOX_MGMT.FINISH.QR.REFRESH') }}
                </NextButton>
                <NextButton outline iris @click="checkStatus">
                  {{ $t('INBOX_MGMT.FINISH.QR.CHECK_STATUS') }}
                </NextButton>
              </div>
            </div>

            <div
              class="status-info p-4 bg-n-slate-2 rounded-lg border border-n-slate-6"
            >
              <div class="text-sm text-n-slate-11 space-y-2">
                <div class="flex justify-center items-center gap-2">
                  <span class="font-medium">
                    {{ $t('INBOX_MGMT.FINISH.QR.STATUS_LABEL') }}
                  </span>
                  <span
                    class="px-2 py-1 rounded-full text-xs font-medium"
                    :class="{
                      'bg-n-teal-2 text-n-teal-11 border border-n-teal-6':
                        qrcodeData.data.status === 'open',
                      'bg-n-amber-2 text-n-amber-11 border border-n-amber-6':
                        qrcodeData.data.status === 'connecting',
                      'bg-n-ruby-2 text-n-ruby-11 border border-n-ruby-6':
                        qrcodeData.data.status === 'close',
                    }"
                  >
                    {{ qrcodeData.data.status }}
                  </span>
                </div>
                <div class="flex justify-center items-center gap-2">
                  <span class="font-medium">
                    {{ $t('INBOX_MGMT.FINISH.QR.CONNECTED_LABEL') }}
                  </span>
                  <span
                    class="px-2 py-1 rounded-full text-xs font-medium"
                    :class="
                      qrcodeData.data.connected
                        ? 'bg-n-teal-2 text-n-teal-11 border border-n-teal-6'
                        : 'bg-n-ruby-2 text-n-ruby-11 border border-n-ruby-6'
                    "
                  >
                    {{
                      qrcodeData.data.connected
                        ? $t('INBOX_MGMT.FINISH.QR.YES')
                        : $t('INBOX_MGMT.FINISH.QR.NO')
                    }}
                  </span>
                </div>
                <div
                  v-if="qrcodeData.data.pairing_code"
                  class="flex justify-center items-center gap-2"
                >
                  <span class="font-medium">
                    {{ $t('INBOX_MGMT.FINISH.QR.PAIRING_CODE') }}
                  </span>
                  <code
                    class="px-2 py-1 bg-n-slate-3 rounded text-xs font-mono"
                  >
                    {{ qrcodeData.data.pairing_code }}
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Web Widget Script -->
        <div class="my-4 mx-auto max-w-[70%]">
          <woot-code
            v-if="currentInbox.web_widget_script"
            :script="currentInbox.web_widget_script"
          />
        </div>

        <!-- Twilio SMS -->
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isATwilioInbox"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>

        <!-- WhatsApp Cloud -->
        <div v-if="isWhatsAppCloudInbox" class="w-[50%] max-w-[50%] ml-[25%]">
          <p class="mt-8 font-medium text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_URL') }}
          </p>
          <woot-code lang="html" :script="currentInbox.callback_webhook_url" />
          <p class="mt-8 font-medium text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_VERIFICATION_TOKEN'
              )
            }}
          </p>
          <woot-code
            lang="html"
            :script="currentInbox.provider_config.webhook_verify_token"
          />
        </div>

        <!-- Line -->
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isALineInbox"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>

        <!-- SMS (Bandwidth) -->
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isASmsInbox"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>

        <!-- Email Forward -->
        <div
          v-if="isAEmailInbox && !currentInbox.provider"
          class="w-[50%] max-w-[50%] ml-[25%]"
        >
          <woot-code lang="html" :script="currentInbox.forward_to_email" />
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-center gap-2 mt-4">
          <router-link
            :to="{
              name: 'settings_inbox_show',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              outline
              slate
              :label="$t('INBOX_MGMT.FINISH.MORE_SETTINGS')"
            />
          </router-link>
          <router-link
            :to="{
              name: 'inbox_dashboard',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              solid
              teal
              :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
            />
          </router-link>
        </div>
      </div>
    </EmptyState>
  </div>
</template>

<style scoped>
.qr-wrapper {
  display: inline-block;
  padding: 1rem;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.qr-image {
  width: 280px;
  height: 280px;
  display: block;
  border-radius: 0.5rem;
}

.qr-placeholder {
  width: 280px;
  height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed rgb(var(--slate-6));
  border-radius: 0.5rem;
  background: rgb(var(--slate-2));
}
</style>
